import { decode } from 'js-base64';
import { NotificationCard } from '../../components/Card';
import { Flex, Icon, Center, Button, Box } from '@chakra-ui/react';
import { Briefcase01 } from '@kula-ai/icons/dual-tone/Briefcase01';
import { NotificationTime } from '../../components/Time';
import {
  RequisitionDetailsSection,
  RequisitionDetailsList,
} from '../requisition-notifications/components';
import { KText } from '@/ui-library/components';
import { ModalWithForm } from '@/components/ats';
import { useApproval } from '@/pages/requisitions/hooks';
import { CheckCircle } from '@kula-ai/icons/dual-tone/CheckCircle';
import { XCircle } from '@kula-ai/icons/dual-tone/XCircle';
import { jobsEmploymentsOptions } from '@/pages/ats-job-creation/tabs/JobInformation/jobInfoHelpers';

const WORKPLACE_OPTIONS = {
  office: 'On-Site',
  remote: 'Remote',
  hybrid: 'Hybrid',
};

const formatWorkplace = (place) => {
  if (!place) return '';
  return WORKPLACE_OPTIONS[place] || place;
};

const formatEmploymentType = (type) => {
  if (!type) return '';
  const option = jobsEmploymentsOptions.find((opt) => opt.value === type);
  return option?.label || type;
};

const JobRequestNotificationWrapper = ({
  notification = {},
  onClose = () => {},
}) => {
  const { custom_attributes } = notification;
  const data = JSON.parse(decode(custom_attributes));
  const {
    job_id,
    job_name,
    requester_name,
    job_workplace,
    job_office_name,
    job_employment_type,
    job_salary_range,
    job_department_name,
    job_description,
    job_requisition_codes,
    job_requisition_value,
  } = data;

  const {
    actionStatus,
    isApproveActionLoading,
    rejectModalActions,
    rejectApproveFields,
    handleApprove,
    handleReject,
    handleRejectSubmit,
  } = useApproval({
    entityId: job_id,
    type: 'job',
  });

  // Format requisition text
  const requisitionText =
    job_requisition_codes && job_requisition_codes.length > 0
      ? job_requisition_codes.join(', ')
      : job_requisition_value || null;

  // Build details data
  const detailsData = [
    { label: 'Job name', value: job_name },
    { label: 'Requisition', value: requisitionText },
    { label: 'Department', value: job_department_name },
    { label: 'Office', value: job_office_name },
    { label: 'Workplace', value: formatWorkplace(job_workplace) },
    { label: 'Work type', value: formatEmploymentType(job_employment_type) },
    { label: 'Salary range', value: job_salary_range },
  ].filter((item) => item.value); // Filter out items with no value

  // Strip HTML from description for display
  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const descriptionText = job_description
    ? stripHtml(job_description)
    : 'No description provided';

  return (
    <>
      <Flex alignItems="baseline" gap="6px">
        <Center minW="34px" h="34px" borderRadius="sm" bg="pastel-2-light">
          <Icon as={Briefcase01} color="pastel-2-dark" width="16px" />
        </Center>
        <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start" flex="1">
          <KText>
            <KText as="span" fontWeight="semiBold">
              {requester_name}
            </KText>{' '}
            requests job approval for{' '}
            <KText as="span" fontWeight="semiBold">
              {job_name}
            </KText>
          </KText>

          <RequisitionDetailsSection title="Details">
            <RequisitionDetailsList data={detailsData} />
          </RequisitionDetailsSection>

          {job_description && (
            <RequisitionDetailsSection
              title="Description"
              defaultIsOpen={false}
            >
              <Box
                p="16px"
                bg="white"
                maxH="200px"
                overflowY="auto"
                sx={{
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'secondary-300',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: 'secondary-400',
                  },
                }}
              >
                <KText size="sm-medium" whiteSpace="pre-wrap">
                  {descriptionText}
                </KText>
              </Box>
            </RequisitionDetailsSection>
          )}

          <Flex flexDir="row" gap="8px">
            {actionStatus === 'approved' ? (
              <Flex alignItems="center" gap="4px">
                <Icon as={CheckCircle} color="pastel-2-dark" width="16px" />
                <KText color="pastel-2-dark" size="sm-medium">
                  Approved
                </KText>
              </Flex>
            ) : actionStatus === 'rejected' ? (
              <Flex alignItems="center" gap="4px">
                <Icon as={XCircle} color="pastel-1-dark" width="16px" />
                <KText color="pastel-1-dark" size="sm-medium">
                  Rejected
                </KText>
              </Flex>
            ) : (
              <>
                <Button
                  variant="primary"
                  onClick={handleApprove}
                  size="sm"
                  isLoading={isApproveActionLoading}
                >
                  Approve
                </Button>
                <Button
                  variant="danger"
                  onClick={handleReject}
                  size="sm"
                  isLoading={isApproveActionLoading}
                >
                  Reject
                </Button>
              </>
            )}
          </Flex>

          <NotificationTime notification={notification} />
        </Flex>
      </Flex>

      {/* Reject Modal */}
      {rejectModalActions.isOpen && (
        <ModalWithForm
          isOpen={rejectModalActions.isOpen}
          onClose={() => rejectModalActions.onClose()}
          headerText="Reason for rejection?"
          subHeaderText="Provide detailed feedback on why you are rejecting this job"
          primaryCtaText="Reject"
          formConfig={rejectApproveFields}
          handleSubmit={handleRejectSubmit}
          isCtaLoading={isApproveActionLoading}
          autoFocus={false}
          formContainerStyles={{
            m: '-10px 0',
          }}
          skipDirtyCheck={true}
        />
      )}
    </>
  );
};

export const JobRequestNotification = ({
  notification = {},
  onClose = () => {},
}) => {
  return (
    <NotificationCard notification={notification}>
      <JobRequestNotificationWrapper
        notification={notification}
        onClose={onClose}
      />
    </NotificationCard>
  );
};
